import { useState, useEffect, useRef } from "react";
import { businessLinesHooks, IBusinessLine } from "@/api/businessLinesApi";
import staffApi from "@/api/staffApi";
import toast from "react-hot-toast";
import { usePermissions } from "@/contexts/PermissionContext";

interface AddStaffModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStaffAdded: () => void;
}

// Status indicator for business lines
const getStatusIndicator = (status: string) => {
  const statusConfig = {
    active: {
      text: "Active",
      color: "text-green-600",
      bgColor: "bg-green-100",
    },
    inactive: {
      text: "Inactive",
      color: "text-gray-600",
      bgColor: "bg-gray-100",
    },
    pending_activation: {
      text: "Pending",
      color: "text-yellow-600",
      bgColor: "bg-yellow-100",
    },
    reserved: {
      text: "Reserved",
      color: "text-blue-600",
      bgColor: "bg-blue-100",
    },
    suspended: {
      text: "Suspended",
      color: "text-red-600",
      bgColor: "bg-red-100",
    },
  };

  const config =
    statusConfig[status as keyof typeof statusConfig] || statusConfig.inactive;

  return (
    <span
      className={`px-2 py-1 text-xs font-medium rounded-full ${config.color} ${config.bgColor}`}
    >
      {config.text}
    </span>
  );
};

const PERMISSION_LEVELS = [
  {
    value: "no_access",
    label: "No access",
    description: "Unable to log in, profile managed by admin(s)",
  },
  {
    value: "standard",
    label: "Standard",
    description:
      "Able to view and manage their own profile, and view My Team, comms and settings (except billing)",
  },
  {
    value: "admin",
    label: "Admin",
    description:
      "Able to view and manage all profiles, comms and account settings",
  },
];

// Custom Business Number Dropdown
const BusinessNumberDropdown = ({
  value,
  onChange,
  options,
  label = "Business number",
  disabled = false,
  showAllNumbers = false,
  loading = false,
  error = null,
  onRetry,
  multiple = false,
  selectedValues = [],
  onMultipleChange,
}: {
  value: string;
  onChange: (val: string) => void;
  options: IBusinessLine[];
  label?: string;
  disabled?: boolean;
  showAllNumbers?: boolean;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  multiple?: boolean;
  selectedValues?: string[];
  onMultipleChange?: (values: string[]) => void;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const selectedOption = multiple
    ? null
    : value === "all"
    ? { phone_number: "Business numbers" }
    : options.find((o) => o.id === value);

  const selectedCount = multiple ? selectedValues.length : 0;
  const displayText = multiple
    ? selectedCount === 0
      ? "Select business numbers"
      : selectedCount === 1
      ? options.find((o) => o.id === selectedValues[0])?.phone_number ||
        "1 number selected"
      : `${selectedCount} numbers selected`
    : selectedOption
    ? selectedOption.phone_number
    : "";

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  if (disabled || loading) {
    return (
      <div className="mb-2 relative">
        <div className="border rounded-lg px-4 py-2 bg-gray-50 flex flex-col">
          <span className="text-xs text-gray-400">{label}</span>
          <span className="text-lg text-gray-500 font-medium">
            {loading
              ? "Loading..."
              : showAllNumbers
              ? "All numbers"
              : "Not applicable"}
          </span>
          {disabled && !loading && (
            <span className="text-xs text-gray-400 mt-1">
              Select "Standard" permission level to choose business lines
            </span>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-2 relative">
        <div className="border rounded-lg px-4 py-2 bg-red-50 flex flex-col">
          <span className="text-xs text-red-400">{label}</span>
          <span className="text-lg text-red-500 font-medium">
            Error loading business lines
          </span>
          {onRetry && (
            <button
              className="text-xs text-red-600 hover:text-red-800 mt-1 text-left"
              onClick={(e) => {
                e.stopPropagation();
                onRetry();
              }}
            >
              Click to retry
            </button>
          )}
        </div>
      </div>
    );
  }

  const handleMultipleSelection = (lineId: string) => {
    if (!onMultipleChange) return;

    const newSelection = selectedValues.includes(lineId)
      ? selectedValues.filter((id) => id !== lineId)
      : [...selectedValues, lineId];

    onMultipleChange(newSelection);
  };

  const handleSelectAll = () => {
    if (!onMultipleChange) return;
    const allIds = options.map((line) => line.id);
    onMultipleChange(allIds);
  };

  const handleDeselectAll = () => {
    if (!onMultipleChange) return;
    onMultipleChange([]);
  };

  return (
    <div className="mb-2 relative" ref={ref}>
      <div
        className={`border rounded-lg px-4 py-2 cursor-pointer bg-white transition-all flex flex-col focus:outline-none ${
          open
            ? "ring-2 ring-green-200 border-green-400"
            : "border-gray-200 hover:border-gray-300"
        }`}
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") setOpen((v) => !v);
        }}
      >
        <span className="text-xs text-gray-400">{label}</span>
        <span className="text-lg text-gray-800 font-medium">{displayText}</span>
        {!multiple &&
          selectedOption &&
          "profile" in selectedOption &&
          selectedOption.profile?.business_name && (
            <span className="text-xs text-gray-500">
              {selectedOption.profile.business_name}
            </span>
          )}
        <span className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
          <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
            <path stroke="#A0AEC0" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </div>
      {open && (
        <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
          {multiple && (
            <>
              <div className="px-4 py-2 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">
                    Select numbers
                  </span>
                  <div className="flex gap-2">
                    <button
                      className="text-xs text-blue-600 hover:text-blue-800"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectAll();
                      }}
                    >
                      Select all
                    </button>
                    <button
                      className="text-xs text-gray-600 hover:text-gray-800"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeselectAll();
                      }}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}

          {!multiple && (
            <div
              className={`px-4 py-2 cursor-pointer hover:bg-gray-50 ${
                value === "all" ? "bg-gray-100 font-semibold" : ""
              }`}
              onClick={() => {
                onChange("all");
                setOpen(false);
              }}
            >
              Business numbers
            </div>
          )}

          {options.map((line) => {
            if (multiple) {
              const isSelected = selectedValues.includes(line.id);
              return (
                <div
                  key={line.id}
                  className="px-4 py-2 cursor-pointer hover:bg-gray-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMultipleSelection(line.id);
                  }}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}} // Handled by onClick
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                      aria-label={`Select ${line.phone_number}${
                        line.profile.business_name
                          ? ` for ${line.profile.business_name}`
                          : ""
                      }`}
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{line.phone_number}</div>
                          {line.profile.business_name && (
                            <div className="text-xs text-gray-500">
                              {line.profile.business_name}
                            </div>
                          )}
                        </div>
                        <div className="ml-2">
                          {getStatusIndicator(line.status)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            } else {
              return (
                <div
                  key={line.id}
                  className={`px-4 py-2 cursor-pointer hover:bg-gray-50 ${
                    value === line.id ? "bg-gray-100 font-semibold" : ""
                  }`}
                  onClick={() => {
                    onChange(line.id);
                    setOpen(false);
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{line.phone_number}</div>
                      {line.profile.business_name && (
                        <div className="text-xs text-gray-500">
                          {line.profile.business_name}
                        </div>
                      )}
                    </div>
                    <div className="ml-2">
                      {getStatusIndicator(line.status)}
                    </div>
                  </div>
                </div>
              );
            }
          })}
        </div>
      )}
    </div>
  );
};

// Custom Permission Level Dropdown
const PermissionLevelDropdown = ({
  value,
  onChange,
  options = PERMISSION_LEVELS,
  label = "Permission level",
}: {
  value: string;
  onChange: (val: string) => void;
  options?: typeof PERMISSION_LEVELS;
  label?: string;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selectedOption = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div className="mb-2 relative py-8" ref={ref}>
      <div
        className={`border rounded-lg px-4 py-2 cursor-pointer bg-white transition-all flex flex-col focus:outline-none ${
          open
            ? "ring-2 ring-green-200 border-green-400"
            : "border-gray-200 hover:border-gray-300"
        }`}
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") setOpen((v) => !v);
        }}
      >
        <span className="text-xs text-gray-400">{label}</span>
        <span className="text-lg text-gray-800 font-medium">
          {selectedOption ? selectedOption.label : ""}
        </span>
        <span className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
          <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
            <path stroke="#A0AEC0" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </div>
      {open && (
        <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
          {options.map((level) => {
            const selected = value === level.value;
            return (
              <div
                key={level.value}
                className={`flex items-start gap-3 px-4 py-3 cursor-pointer transition-all ${
                  selected ? "bg-gray-50" : "hover:bg-gray-50"
                }`}
                onClick={() => {
                  onChange(level.value);
                  setOpen(false);
                }}
              >
                <span className="mt-1">
                  <span
                    className={`inline-flex w-5 h-5 rounded-full border-2 items-center justify-center transition-all ${
                      selected ? "border-green-500" : "border-gray-300"
                    }`}
                  >
                    {selected && (
                      <span className="w-3 h-3 bg-green-500 rounded-full block"></span>
                    )}
                  </span>
                </span>
                <span className="flex flex-col text-left">
                  <span className="font-medium text-gray-900">
                    {level.label}
                  </span>
                  <span className="text-xs text-gray-500">
                    {level.description}
                  </span>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default function AddStaffModal({
  isOpen,
  onClose,
  onStaffAdded,
}: AddStaffModalProps) {
  const { permissionLevel: currentUserPermissionLevel } = usePermissions();
  const [businessNumbers, setBusinessNumbers] = useState<IBusinessLine[]>([]);
  const [staffFields, setStaffFields] = useState({
    first_name: "",
    last_name: "",
    email: "",
    permission_level: "no_access",
    all_business_lines_permitted: true,
    permitted_business_lines: [] as string[],
  });
  const [loading, setLoading] = useState(false);
  const [permOpen, setPermOpen] = useState(false);
  const permDropdownRef = useRef<HTMLDivElement>(null);

  // Use React Query hook to fetch business lines
  const {
    data: businessLinesData,
    isLoading: businessLinesLoading,
    error: businessLinesError,
    refetch: refetchBusinessLines,
  } = businessLinesHooks.useActiveBusinessLines();

  // Update local state when data changes
  useEffect(() => {
    if (businessLinesData) {
      let filteredLines = businessLinesData;

      // Always hide pending_activation and reserved numbers
      filteredLines = businessLinesData.filter(
        (line: IBusinessLine) =>
          line.status !== "pending_activation" && line.status !== "reserved"
      );

      // If current user is owner and assigning to admin, only show active and inactive lines
      if (
        currentUserPermissionLevel === "owner" &&
        staffFields.permission_level === "admin"
      ) {
        filteredLines = filteredLines.filter(
          (line: IBusinessLine) =>
            line.status === "active" || line.status === "inactive"
        );
      }

      setBusinessNumbers(filteredLines);
    }
  }, [
    businessLinesData,
    currentUserPermissionLevel,
    staffFields.permission_level,
  ]);

  // Function to retry fetching business lines
  const fetchBusinessNumbers = async () => {
    try {
      await refetchBusinessLines();
    } catch (error) {
      console.error("Error fetching business lines:", error);
    }
  };

  useEffect(() => {
    if (!permOpen) return;
    function handleClick(e: MouseEvent) {
      if (
        permDropdownRef.current &&
        !permDropdownRef.current.contains(e.target as Node)
      ) {
        setPermOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [permOpen]);

  const handleFieldChange = (field: string, value: any) => {
    if (field === "permission_level") {
      // If changing to admin, set all_business_lines_permitted to true
      if (value === "admin") {
        setStaffFields((prev) => ({
          ...prev,
          [field]: value,
          all_business_lines_permitted: true,
          permitted_business_lines: [],
        }));
      } else if (value === "standard") {
        // If changing to standard, reset business line permissions
        setStaffFields((prev) => ({
          ...prev,
          [field]: value,
          all_business_lines_permitted: false,
          permitted_business_lines: [],
        }));
      } else {
        setStaffFields((prev) => ({ ...prev, [field]: value }));
      }
    } else {
      setStaffFields((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleSave = async () => {
    console.log(staffFields);
    setLoading(true);
    try {
      // Validate all required fields
      if (!staffFields.first_name.trim()) {
        toast.error("First name is required");
        setLoading(false);
        return;
      }

      if (!staffFields.last_name.trim()) {
        toast.error("Last name is required");
        setLoading(false);
        return;
      }

      if (!staffFields.email.trim()) {
        toast.error("Email is required");
        setLoading(false);
        return;
      }

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(staffFields.email.trim())) {
        toast.error("Please enter a valid email address");
        setLoading(false);
        return;
      }

      if (!staffFields.permission_level) {
        toast.error("Permission level is required");
        setLoading(false);
        return;
      }

      // Validate business lines for standard permission level
      if (
        staffFields.permission_level === "standard" &&
        !staffFields.all_business_lines_permitted &&
        staffFields.permitted_business_lines.length === 0
      ) {
        toast.error(
          "Please select at least one business line for standard permission level"
        );
        setLoading(false);
        return;
      }

      await staffApi.addStaff({
        email: staffFields.email.trim(),
        permission_level: staffFields.permission_level,
        all_business_lines_permitted: staffFields.all_business_lines_permitted,
        permitted_business_lines: staffFields.permitted_business_lines,
        first_name: staffFields.first_name.trim(),
        last_name: staffFields.last_name.trim(),
      });
      onStaffAdded();
      onClose();
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-20">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-xl p-6 relative">
        <button
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          onClick={onClose}
        >
          <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
            <path
              stroke="currentColor"
              strokeWidth="2"
              d="M6 6l12 12M6 18L18 6"
            />
          </svg>
        </button>
        <h2 className="text-xl font-semibold mb-4">Add team members</h2>
        <div className="mb-6 border-b pb-4 last:border-b-0 last:pb-0">
          <div className="flex gap-2 mb-2">
            <input
              className="flex-1 border rounded px-3 py-2 text-sm"
              placeholder="First name"
              value={staffFields.first_name}
              onChange={(e) => handleFieldChange("first_name", e.target.value)}
            />
            <input
              className="flex-1 border rounded px-3 py-2 text-sm"
              placeholder="Last name"
              value={staffFields.last_name}
              onChange={(e) => handleFieldChange("last_name", e.target.value)}
            />
          </div>
          <div className="mb-2">
            <input
              className="w-full border rounded px-3 py-2 text-sm"
              placeholder="Email"
              value={staffFields.email}
              onChange={(e) => handleFieldChange("email", e.target.value)}
            />
          </div>
          {/* Permission Level Dropdown */}
          <PermissionLevelDropdown
            value={staffFields.permission_level}
            onChange={(val) => handleFieldChange("permission_level", val)}
          />
          {/* Business Numbers Dropdown */}
          {staffFields.permission_level === "no_access" && (
            <div className="text-xs text-gray-500 mb-2">
              💡 Change permission level to "Standard" to select specific
              business lines
            </div>
          )}
          {staffFields.permission_level === "standard" && (
            <div className="text-xs text-blue-500 mb-2">
              💡 You can select multiple business numbers for this user
            </div>
          )}
          <BusinessNumberDropdown
            value={
              staffFields.all_business_lines_permitted
                ? "all"
                : staffFields.permitted_business_lines[0] || ""
            }
            onChange={(val) => {
              if (val === "all") {
                handleFieldChange("all_business_lines_permitted", true);
                handleFieldChange("permitted_business_lines", []);
              } else {
                handleFieldChange("all_business_lines_permitted", false);
                handleFieldChange("permitted_business_lines", [val]);
              }
            }}
            options={businessNumbers}
            disabled={
              staffFields.permission_level === "no_access" ||
              staffFields.permission_level === "admin"
            }
            showAllNumbers={staffFields.permission_level === "admin"}
            loading={businessLinesLoading}
            error={businessLinesError ? "Failed to load business lines" : null}
            onRetry={fetchBusinessNumbers}
            multiple={staffFields.permission_level === "standard"}
            selectedValues={staffFields.permitted_business_lines}
            onMultipleChange={(values) => {
              handleFieldChange("permitted_business_lines", values);
              // Always set all_business_lines_permitted to false when selecting specific lines
              handleFieldChange("all_business_lines_permitted", false);
            }}
          />
          {staffFields.permission_level === "standard" &&
            staffFields.permitted_business_lines.length === 0 && (
              <p className="mt-2 text-xs text-amber-600">
                ⚠️ At least one business line must be selected for standard
                permission level
              </p>
            )}
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button
            className="px-6 py-2 rounded bg-gray-100 text-gray-600 hover:bg-gray-200"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="px-6 py-2 rounded bg-green-500 text-white hover:bg-green-600"
            onClick={handleSave}
            disabled={loading}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
